/* <video-autoplay> — plays only the video the viewport actually shows, and
 * only once the section is near the viewport.
 *
 * sections/video.liquid (autoplay mode) renders a desktop <video> and,
 * optionally, a mobile one, both with preload="none" and no `autoplay`
 * attribute. Craft's small-hide / medium-hide utilities hide one of them by
 * CSS, but a browser downloads every <video autoplay> in the DOM regardless
 * of display — two full files on every page load, measured at 6 + 6.5 MB on
 * the SpringRopes product page. This element picks the one that matches the
 * 750px breakpoint, tells the browser to load it, and calls play(); the other
 * never loads. Re-evaluated when the breakpoint flips (rotation, resize).
 *
 * 2026-09-17 — the remaining download was still started at page load, while
 * the section sits 4th on the SpringRopes page, well below the fold on
 * mobile. On a throttled connection the 6.5 MB file competed with the LCP
 * image (PSI mobile: LCP 8.7 s, 10.3 MB total). An IntersectionObserver now
 * gates _sync(): nothing loads until a video container comes within 300px of
 * the viewport; leaving the viewport pauses, coming back plays. The observer
 * watches the .video-section__media--autoplay containers, not this element:
 * <video-autoplay> is `display: contents`, has no box, and would never
 * intersect. The CSS-hidden container cannot intersect either, so only the
 * visible one drives the state. Without IntersectionObserver (very old
 * browsers) the previous behaviour applies: load and play immediately.
 *
 * play() rejects when the platform refuses autoplay (iOS Low Power Mode, a
 * "block autoplay" setting). The poster stays; that is the pre-existing
 * behaviour and not an error, so the rejection is swallowed.
 */
(function () {
  if (!('customElements' in window) || customElements.get('video-autoplay')) return;

  customElements.define('video-autoplay', class extends HTMLElement {
    connectedCallback() {
      this._mq = window.matchMedia('(min-width: 750px)');
      this._sync = this._sync.bind(this);
      if (this._mq.addEventListener) this._mq.addEventListener('change', this._sync);
      else this._mq.addListener(this._sync);
      // Chrome pauses muted video in a background tab and does not always
      // resume it; a page opened in a background tab would otherwise show a
      // still frame when the shopper switches to it.
      this._onVisible = function () { if (!document.hidden) this._sync(); }.bind(this);
      document.addEventListener('visibilitychange', this._onVisible);

      this._near = false;
      var containers = this.querySelectorAll('.video-section__media--autoplay');
      if ('IntersectionObserver' in window && containers.length) {
        this._io = new IntersectionObserver(function (entries) {
          // Any visible container near the viewport means "near"; the one
          // hidden by CSS never reports isIntersecting.
          var near = false;
          entries.forEach(function (e) { if (e.isIntersecting) near = true; });
          if (near !== this._near) { this._near = near; this._sync(); }
        }.bind(this), { rootMargin: '300px 0px' });
        containers.forEach(function (c) { this._io.observe(c); }, this);
      } else {
        this._near = true;
        this._sync();
      }
    }

    disconnectedCallback() {
      if (!this._mq) return;
      if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._sync);
      else this._mq.removeListener(this._sync);
      document.removeEventListener('visibilitychange', this._onVisible);
      if (this._io) this._io.disconnect();
      this.querySelectorAll('video').forEach(function (v) { v.pause(); });
    }

    _sync() {
      var videos = Array.prototype.slice.call(this.querySelectorAll('video[data-video-autoplay]'));
      if (!this._near) {
        videos.forEach(function (v) { if (!v.paused) v.pause(); });
        return;
      }
      var hasMobile = videos.some(function (v) { return v.getAttribute('data-video-autoplay') === 'mobile'; });
      var want = (!this._mq.matches && hasMobile) ? 'mobile' : 'desktop';

      videos.forEach(function (v) {
        if (v.getAttribute('data-video-autoplay') !== want) {
          if (!v.paused) v.pause();
          return;
        }
        if (v.preload === 'none') v.preload = 'auto';
        // Muted is required for autoplay; the attribute is set in Liquid, but
        // Safari has been seen to drop it on a preload="none" element.
        v.muted = true;
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      });
    }
  });
})();
